import { getBetOddFactor, type BetslipTicket } from './betslip';
import { BonusType } from './bonuses';
import { TicketType } from './tickets';

/**
 * UsersAPI `TicketGamesTypes`. Mixed prematch + live is Combo.
 * Matches `TicketsCreateService.GetTicketGameType`.
 */
export const TicketGamesType = {
  Prematch: 0,
  Live: 1,
  Combo: 2,
} as const;

export type TicketGamesType = (typeof TicketGamesType)[keyof typeof TicketGamesType];

/** UsersAPI `AccumulatorSettings` — one attractive-combi tier. */
export interface AccumulatorSettings {
  MinBets: number;
  Percent?: number | null;
  Amount?: number | null;
}

/**
 * UsersAPI `AvailableTicketBonus` from login `Bonuses`.
 * Attractive combi uses `BetSettings`; money-back uses the flat fields.
 */
export interface AvailableTicketBonus {
  Id?: string | null;
  Type?: number | null;
  BetSettings?: AccumulatorSettings[] | null;
  Bets?: number | null;
  Quota?: number | null;
  Stake?: number | null;
  Percent?: number | null;
  Amount?: number | null;
  Name?: string | null;
  GameType?: number | null;
}

/** Below the first tier: prompt the player to add selections. */
export interface CombiBonusProgress {
  kind: 'progress';
  selectionsNeeded: number;
  /** First-tier percent, when the tier pays a percent. */
  nextPercent: number | null;
  /** First-tier fixed amount, when the tier has no percent. */
  nextAmount: number | null;
}

/** Exact `MinBets` match — extra win the API will add. */
export interface CombiBonusApplied {
  kind: 'applied';
  percent: number | null;
  amount: number | null;
  /** Added on top of the base possible win. Not stored on the slip. */
  bonusValue: number;
  attractiveCombiId?: string;
}

export type CombiBonusState = CombiBonusProgress | CombiBonusApplied;

export interface CombiBonusPlace {
  /** Base ticket, or a copy whose `PossibleWin` includes the bonus. */
  ticket: BetslipTicket;
  attractiveCombiId?: string;
}

function finiteNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function readTiers(bonus: AvailableTicketBonus): AccumulatorSettings[] {
  if (!Array.isArray(bonus.BetSettings)) return [];
  return bonus.BetSettings.filter(
    (tier) => tier != null && typeof tier.MinBets === 'number' && Number.isFinite(tier.MinBets),
  );
}

function isLiveEvent(live: BetslipTicket['Items'][number]['Event']['Live']): boolean {
  return live === true || live === 'true' || live === '1';
}

/** Prematch / live / mixed, counted per leg the way the ticket API does. */
export function ticketGamesType(ticket: BetslipTicket): TicketGamesType {
  let prematch = 0;
  let live = 0;
  for (const item of ticket.Items) {
    const legs = item.Bets.length;
    if (isLiveEvent(item.Event.Live)) live += legs;
    else prematch += legs;
  }
  if (live > 0 && prematch > 0) return TicketGamesType.Combo;
  if (live > 0) return TicketGamesType.Live;
  return TicketGamesType.Prematch;
}

function qualifiedBetCount(ticket: BetslipTicket, quota: number | null): number {
  let count = 0;
  for (const item of ticket.Items) {
    for (const bet of item.Bets) {
      if (quota != null && getBetOddFactor(bet) < quota) continue;
      count += 1;
    }
  }
  return count;
}

function hasMultipleBetsOnEvent(ticket: BetslipTicket): boolean {
  return ticket.Items.some((item) => item.Bets.length > 1);
}

function firstAttractiveCombi(
  bonuses: AvailableTicketBonus[] | null | undefined,
): AvailableTicketBonus | null {
  if (!bonuses?.length) return null;
  return (
    bonuses.find((bonus) => Number(bonus?.Type) === BonusType.AttractiveCombi) ?? null
  );
}

/**
 * Resolve the attractive combi bonus for display.
 * Returns null unless the slip is a combi the API would actually pay:
 * one bet per event, optional minimum odd, optional game type, and an exact tier.
 * Below the first tier, returns a progress prompt instead of a payout.
 * The stored slip is not modified.
 */
export function resolveCombiBonus(
  ticket: BetslipTicket,
  bonuses: AvailableTicketBonus[] | null | undefined,
): CombiBonusState | null {
  if (ticket.Type !== TicketType.Combi) return null;

  const bonus = firstAttractiveCombi(bonuses);
  if (!bonus) return null;

  const tiers = readTiers(bonus);
  const first = tiers[0];
  if (!first) return null;

  if (hasMultipleBetsOnEvent(ticket)) return null;

  const gameType = finiteNumber(bonus.GameType);
  if (gameType != null && gameType !== ticketGamesType(ticket)) return null;

  const quota = finiteNumber(bonus.Quota);
  const qualified = qualifiedBetCount(ticket, quota);

  if (qualified < first.MinBets) {
    const nextPercent = finiteNumber(first.Percent);
    return {
      kind: 'progress',
      selectionsNeeded: first.MinBets - qualified,
      nextPercent,
      nextAmount: nextPercent == null ? finiteNumber(first.Amount) : null,
    };
  }

  const tier = tiers.find((entry) => entry.MinBets === qualified);
  if (!tier) return null;

  const percent = finiteNumber(tier.Percent);
  const amount = finiteNumber(tier.Amount);
  const baseWin = ticket.PossibleWin ?? 0;
  const bonusValue = percent != null ? (baseWin * percent) / 100 : (amount ?? 0);
  const id = typeof bonus.Id === 'string' && bonus.Id.trim() ? bonus.Id : undefined;

  return {
    kind: 'applied',
    percent,
    amount,
    bonusValue,
    attractiveCombiId: id,
  };
}

/**
 * Copy of the ticket with the combi bonus added to possible win, for place-time
 * validation and the success snapshot. The stored slip is left unchanged.
 */
export function withCombiBonus(
  ticket: BetslipTicket,
  bonuses: AvailableTicketBonus[] | null | undefined,
): CombiBonusPlace {
  const resolved = resolveCombiBonus(ticket, bonuses);
  if (!resolved || resolved.kind !== 'applied') return { ticket };
  return {
    ticket: {
      ...ticket,
      PossibleWin: (ticket.PossibleWin ?? 0) + resolved.bonusValue,
    },
    attractiveCombiId: resolved.attractiveCombiId,
  };
}
